import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Center, Flex, Spinner, Text } from '@chakra-ui/react';
import { MdFilterList } from 'react-icons/md';
import { HiMagnifyingGlass } from 'react-icons/hi2';
import { Button as ButtonComponent, SearchInput } from '@themes/aegix';
import { useAlertHistoryDrawerData } from '../../../hooks/queries/useAlerts';
import type { IncidentDetailItem } from '../../../services/alerts.service';
import { AlertHistoryEventsTable } from './AlertHistoryEventsTable';
import { AlertHistoryFilterDialog } from './AlertHistoryFilterDialog';

const PAGE_LIMIT = 20;

interface AlertHistoryEventsTabProps {
  alertId: number;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  debouncedQuery: string;
  incidentTypes: string;
  onIncidentTypesChange: (csv: string) => void;
  onResultCountChange?: (count: number) => void;
}

/**
 * Events tab content: search input, filter trigger, paginated event table.
 * State is controlled from the parent drawer so the drawer header's
 * conditional download button can read the same `incidentTypes` + `query`
 * the table is currently driven by.
 */
export function AlertHistoryEventsTab({
  alertId,
  searchValue,
  onSearchValueChange,
  debouncedQuery,
  incidentTypes,
  onIncidentTypesChange,
  onResultCountChange,
}: AlertHistoryEventsTabProps) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<IncidentDetailItem[]>([]);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Reset paging whenever query, filter, or alert changes.
  useEffect(() => {
    setOffset(0);
    setItems([]);
  }, [debouncedQuery, incidentTypes, alertId]);

  const payload = {
    id: alertId,
    limit: PAGE_LIMIT,
    offset,
    incident_types: incidentTypes,
    query: debouncedQuery,
  };
  const { data, isLoading } = useAlertHistoryDrawerData(payload, true);

  // Append/replace pattern: offset 0 → replace, else append.
  useEffect(() => {
    const newRows = data?.history_data?.results;
    if (!newRows) return;
    setItems((prev) => {
      const next = offset === 0 ? newRows : [...prev, ...newRows];
      onResultCountChange?.(next.length);
      return next;
    });
  }, [data, offset, onResultCountChange]);

  // Infinite-scroll trigger via IntersectionObserver — replaces AIM's
  // PaginationScroll module (which lives in app-internal AIM code).
  const hasMore = !!data?.history_data?.next;
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || isLoading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setOffset((prev) => prev + PAGE_LIMIT);
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, items.length]);

  return (
    <Flex direction="column" minH={0} flex={1} pt={3}>
      <Flex justify="flex-end" mb={3} gap={2} align="center">
        <SearchInput
          visual="outline"
          aria-label={t('events.searchEvents', 'Search events')}
          name="search"
          value={searchValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onSearchValueChange(e.target.value)
          }
          startElement={<HiMagnifyingGlass size={16} />}
          placeholder={t('events.searchEvents', 'Search events')}
          css={{ width: '200px' }}
        />
        <ButtonComponent
          visual="secondary"
          onClick={() => setFilterDialogOpen(true)}
          aria-label={t('events.filterEventTypes', 'Filter event types')}
        >
          <MdFilterList />
        </ButtonComponent>
      </Flex>

      <Box
        flex={1}
        minH={0}
        overflowY="auto"
        border="1px solid"
        borderColor="border.subtle"
        borderRadius="md"
      >
        {items.length === 0 && !isLoading ? (
          <Center py={10}>
            <Text color="text.secondary">
              {t('events.noEventsRecorded', 'No events recorded for this alert.')}
            </Text>
          </Center>
        ) : (
          <AlertHistoryEventsTable rows={items} />
        )}

        {isLoading && (
          <Center py={4}>
            <Spinner size="sm" />
          </Center>
        )}

        {hasMore && <Box ref={sentinelRef} h={1} />}
      </Box>

      <AlertHistoryFilterDialog
        isOpen={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        onApply={onIncidentTypesChange}
      />
    </Flex>
  );
}
